import React, { lazy } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { AuthGateProvider } from "@/context/AuthGate";
import { RatingStoreProvider } from "@/components/ratings/ratingStore";
import { RadioPlayerProvider } from "@/components/radio/radioPlayer";
import ProtectedRoute from "@/routes/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";

// Public
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";

// App shell + pages (Suspense boundary is inside AppShell for smoother swaps)
import AppShell from "@/components/layout/AppShell";
import GuestGate from "@/components/common/GuestGate";
const Dashboard = lazy(() => import("@/pages/app/Dashboard"));
const Songwriters = lazy(() => import("@/pages/app/Songwriters"));
const MusicIndustry = lazy(() => import("@/pages/app/MusicIndustry"));
const Services = lazy(() => import("@/pages/app/Services"));
const Merchandising = lazy(() => import("@/pages/app/Merchandising"));
const Radio = lazy(() => import("@/pages/app/Radio"));
const Workshops = lazy(() => import("@/pages/app/Workshops"));
const Messages = lazy(() => import("@/pages/app/Messages"));
const Notifications = lazy(() => import("@/pages/app/Notifications"));
const Profile = lazy(() => import("@/pages/app/Profile"));
const Settings = lazy(() => import("@/pages/app/Settings"));
const Admin = lazy(() => import("@/pages/app/Admin"));
const WaitlistAdmin = lazy(() => import("@/pages/app/WaitlistAdmin"));
const AnalyticsAdmin = lazy(() => import("@/pages/app/AnalyticsAdmin"));
const BillingAdmin = lazy(() => import("@/pages/app/admin/BillingAdmin"));
const ReportsAdmin = lazy(() => import("@/pages/app/admin/ReportsAdmin"));
const ModerationAdmin = lazy(() => import("@/pages/app/admin/ModerationAdmin"));
const ApiAdmin = lazy(() => import("@/pages/app/admin/ApiAdmin"));
const StorageAdmin = lazy(() => import("@/pages/app/admin/StorageAdmin"));
const UsersAdmin = lazy(() => import("@/pages/app/admin/UsersAdmin"));
const RatingsAdmin = lazy(() => import("@/pages/app/admin/RatingsAdmin"));
const WaiversAdmin = lazy(() => import("@/pages/app/admin/WaiversAdmin"));
const SongsAdmin = lazy(() => import("@/pages/app/admin/SongsAdmin"));
const SipAdmin = lazy(() => import("@/pages/app/admin/SipAdmin"));
const SystemAdmin = lazy(() => import("@/pages/app/admin/SystemAdmin"));
import MyPublicProfileRedirect from "@/pages/app/MyPublicProfileRedirect";

function AdminAliasRedirect() {
  const { pathname, search } = window.location;
  return <Navigate to={`/app${pathname}${search}`} replace />;
}

// Public shareable pages (Phase 3A)
const PublicSongwriter = lazy(() => import("@/pages/public/PublicSongwriter"));
const PublicSong = lazy(() => import("@/pages/public/PublicSong"));
const SIP = lazy(() => import("@/pages/public/SIP"));
const FlavorSphere = lazy(() => import("@/pages/public/FlavorSphere"));
const SearchPage = lazy(() => import("@/pages/public/SearchPage"));
// Guest Studio (Phase 3B) — build a profile / song draft without signing in.
const GuestProfile = lazy(() => import("@/pages/guest/GuestProfile"));
const GuestSong = lazy(() => import("@/pages/guest/GuestSong"));

/**
 * Handles Emergent OAuth callback fragment (session_id=...) BEFORE Router
 * renders the target route.
 */
function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Public pillar routes — the four corner hotspots on the landing
          logo route to these. They forward into the authenticated shell. */}
      <Route path="/songwriters" element={<Navigate to="/app/songwriters" replace />} />
      <Route path="/music-industry" element={<Navigate to="/app/music-industry" replace />} />
      <Route path="/merchandising" element={<Navigate to="/app/merchandising" replace />} />
      <Route path="/services" element={<Navigate to="/app/services" replace />} />
      <Route path="/radio" element={<Navigate to="/app/radio" replace />} />
      <Route path="/profile/edit" element={<Navigate to="/app/profile" replace />} />
      <Route path="/admin/*" element={<AdminAliasRedirect />} />

      {/* Public shareable pages — Phase 3A (guest-viewable, SEO-friendly) */}
      <Route path="/songwriter/:username" element={<React.Suspense fallback={null}><PublicSongwriter /></React.Suspense>} />
      <Route path="/song/:slug" element={<React.Suspense fallback={null}><PublicSong /></React.Suspense>} />
      <Route path="/sip/:slug" element={<React.Suspense fallback={null}><SIP /></React.Suspense>} />
      <Route path="/flavor" element={<React.Suspense fallback={null}><FlavorSphere /></React.Suspense>} />
      <Route path="/search" element={<React.Suspense fallback={null}><SearchPage /></React.Suspense>} />

      {/* Guest Studio — no auth required. */}
      <Route path="/guest/profile" element={<React.Suspense fallback={null}><GuestProfile /></React.Suspense>} />
      <Route path="/guest/song" element={<React.Suspense fallback={null}><GuestSong /></React.Suspense>} />

      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="songwriters" element={<Songwriters />} />
        <Route path="music-industry" element={<MusicIndustry />} />
        <Route path="services" element={<Services />} />
        <Route path="merchandising" element={<Merchandising />} />
        <Route path="radio" element={<Radio />} />
        <Route path="workshops" element={<Workshops />} />
        <Route path="messages" element={<GuestGate area="Messages" testId="guest-gate-messages"><Messages /></GuestGate>} />
        <Route path="notifications" element={<GuestGate area="Notifications" testId="guest-gate-notifications"><Notifications /></GuestGate>} />
        <Route path="profile" element={<GuestGate area="Your Profile" testId="guest-gate-profile"><Profile /></GuestGate>} />
        <Route path="profile/view" element={<GuestGate area="Your Profile" testId="guest-gate-profile-view"><MyPublicProfileRedirect /></GuestGate>} />
        <Route path="settings" element={<GuestGate area="Settings" testId="guest-gate-settings"><Settings /></GuestGate>} />

        {/* Legacy sub-routes redirect to their consolidated pillar page. */}
        <Route path="songs" element={<Navigate to="/app/songwriters" replace />} />
        <Route path="songs/*" element={<Navigate to="/app/songwriters" replace />} />
        <Route path="discover" element={<Navigate to="/app/music-industry" replace />} />
        <Route path="industry" element={<Navigate to="/app/music-industry" replace />} />
        <Route path="ai" element={<Navigate to="/app/songwriters" replace />} />
        <Route path="messages/new" element={<Navigate to="/app/messages" replace />} />
        <Route path="services/marketplace" element={<Navigate to="/app/services" replace />} />
        <Route path="merchandising/marketplace" element={<Navigate to="/app/merchandising" replace />} />
        <Route path="radio/stations" element={<Navigate to="/app/radio" replace />} />
        <Route path="workshops/live" element={<Navigate to="/app/workshops" replace />} />

        <Route
          path="admin"
          element={
            <ProtectedRoute requireAdmin>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/waitlist"
          element={<ProtectedRoute requireAdmin><WaitlistAdmin /></ProtectedRoute>}
        />
        <Route
          path="admin/analytics"
          element={<ProtectedRoute requireAdmin><AnalyticsAdmin /></ProtectedRoute>}
        />
        <Route
          path="admin/billing"
          element={<ProtectedRoute requireAdmin><BillingAdmin /></ProtectedRoute>}
        />
        <Route
          path="admin/reports"
          element={<ProtectedRoute requireAdmin><ReportsAdmin /></ProtectedRoute>}
        />
        <Route
          path="admin/moderation"
          element={<ProtectedRoute requireAdmin><ModerationAdmin /></ProtectedRoute>}
        />
        <Route
          path="admin/api"
          element={<ProtectedRoute requireAdmin><ApiAdmin /></ProtectedRoute>}
        />
        <Route
          path="admin/storage"
          element={<ProtectedRoute requireAdmin><StorageAdmin /></ProtectedRoute>}
        />
        <Route
          path="admin/users"
          element={<ProtectedRoute requireAdmin><UsersAdmin /></ProtectedRoute>}
        />
        <Route path="admin/ratings" element={<ProtectedRoute requireAdmin><RatingsAdmin /></ProtectedRoute>} />
        <Route path="admin/waivers" element={<ProtectedRoute requireAdmin><WaiversAdmin /></ProtectedRoute>} />
        <Route path="admin/songs" element={<ProtectedRoute requireAdmin><SongsAdmin /></ProtectedRoute>} />
        <Route path="admin/sip" element={<ProtectedRoute requireAdmin><SipAdmin /></ProtectedRoute>} />
        <Route path="admin/system" element={<ProtectedRoute requireAdmin><SystemAdmin /></ProtectedRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AuthGateProvider>
          <RatingStoreProvider><RadioPlayerProvider>
            <AppRoutes />
            <Toaster
              theme="dark"
              position="top-right"
              toastOptions={{
                style: {
                  background: "#141414",
                  border: "1px solid rgba(212,175,55,0.35)",
                  color: "#fff",
                  borderRadius: 0,
                },
              }}
            />
          </RadioPlayerProvider></RatingStoreProvider>
        </AuthGateProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
